import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Music, Mail, User, MessageSquare, Link as LinkIcon, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    demo_type: "",
    soundcloud_link: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message envoyé avec succès !");
      setSubmitted(true);
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setLoading(false);
    }
  };

  const demoTypes = [
    { value: "", label: "Sélectionnez un type" },
    { value: "original", label: "Production originale" },
    { value: "remix", label: "Remix" },
    { value: "ep", label: "EP / Album" },
    { value: "collaboration", label: "Demande de collaboration" },
    { value: "other", label: "Autre" }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen py-12 px-6 md:px-12 flex items-center justify-center" data-testid="contact-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-[#FFB347]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#FFB347]" />
          </div>
          <h2 className="font-display text-3xl font-bold mb-4">Message Envoyé !</h2>
          <p className="text-[#A8A29E] mb-8">
            Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: "",
                email: "",
                subject: "",
                message: "",
                demo_type: "",
                soundcloud_link: ""
              });
            }}
            className="btn-secondary"
            data-testid="send-another-btn"
          >
            Envoyer un autre message
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6 md:px-12" data-testid="contact-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 text-center"
        >
          <span className="font-heading text-sm tracking-widest text-[#FFB347] mb-2 block">
            CONTACT
          </span>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">
            Envoyez-nous un Message
          </h1>
          <p className="text-[#A8A29E] text-lg max-w-2xl mx-auto">
            Une question ? Une démo à nous soumettre ? N'hésitez pas à nous contacter. Nous répondons à toutes les demandes.
          </p>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#141210] rounded-2xl border border-[rgba(139,69,19,0.2)] p-8 md:p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            {/* Name & Email Row */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#A8A29E] mb-2">
                  Nom complet *
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="input-field pl-12"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A8A29E] mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.com"
                    className="input-field pl-12"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-[#A8A29E] mb-2">
                Sujet *
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Objet de votre message"
                  className="input-field pl-12"
                  required
                  data-testid="subject-input"
                />
              </div>
            </div>

            {/* Demo Section */}
            <div className="p-6 bg-[#0A0908] rounded-xl border border-[rgba(139,69,19,0.2)]">
              <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
                <Music className="w-5 h-5 text-[#FFB347]" />
                Soumission de Démo (optionnel)
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#A8A29E] mb-2">
                    Type de production
                  </label>
                  <select
                    name="demo_type"
                    value={formData.demo_type}
                    onChange={handleChange}
                    className="input-field appearance-none cursor-pointer"
                    data-testid="demo-type-select"
                  >
                    {demoTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#A8A29E] mb-2">
                    Lien SoundCloud
                  </label>
                  <div className="relative">
                    <LinkIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
                    <input
                      type="url"
                      name="soundcloud_link"
                      value={formData.soundcloud_link}
                      onChange={handleChange}
                      placeholder="https://soundcloud.com/..."
                      className="input-field pl-12"
                      data-testid="soundcloud-link-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-[#A8A29E] mb-2">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Décrivez votre projet, vos inspirations, votre parcours musical..."
                rows={6}
                className="input-field resize-none"
                required
                data-testid="message-textarea"
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-[#0A0908] border-t-transparent rounded-full animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Envoyer le Message
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 grid md:grid-cols-3 gap-6"
        >
          <div className="p-6 bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] text-center">
            <Mail className="w-8 h-8 text-[#FFB347] mx-auto mb-3" />
            <h4 className="font-display font-semibold mb-1">Email</h4>
            <p className="text-[#A8A29E] text-sm">contact@sapito-records.com</p>
          </div>

          <div className="p-6 bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] text-center">
            <Music className="w-8 h-8 text-[#FFB347] mx-auto mb-3" />
            <h4 className="font-display font-semibold mb-1">SoundCloud</h4>
            <a 
              href="https://soundcloud.com/sapito-records"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#A8A29E] text-sm hover:text-[#FFB347] transition-colors"
            >
              @sapito-records
            </a>
          </div>

          <div className="p-6 bg-[#141210] rounded-xl border border-[rgba(139,69,19,0.2)] text-center">
            <MessageSquare className="w-8 h-8 text-[#FFB347] mx-auto mb-3" />
            <h4 className="font-display font-semibold mb-1">Réponse</h4>
            <p className="text-[#A8A29E] text-sm">Sous 48-72h</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactPage;
